import React from 'react';
import { motion } from 'framer-motion';
import { images } from '../../../shared/assets/images';

const zones = [
  {
    name: 'Zona infantil',
    age: '3-9 años',
    accent: 'from-yellow-100 via-white to-orange-50',
    image: images.kidsZone,
    alt: 'Normas de zona infantil',
    description: 'Espacio pensado para peques con acceso limitado por edad para mantener una experiencia segura y adecuada a su etapa.',
    notes: [
      'Acceso exclusivo para niños de 3-9 años.',
      'No se permite el acceso a edades superiores por seguridad.',
    ],
  },
  {
    name: 'Zona juvenil',
    age: '6 a adultos',
    accent: 'from-cyan-100 via-white to-sky-50',
    image: images.youthZone,
    alt: 'Normas de zona juvenil',
    description: 'Área diseñada para una franja mayor, con dinámica propia y normas específicas para un uso responsable de las instalaciones.',
    notes: [
      'Diseñada a partir de 6 años hasta adultos.',
      'Normas específicas para un uso responsable de las instalaciones.',
    ],
  },
];

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export default function RestrictionPage() {
  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.15),_transparent_22%),radial-gradient(circle_at_top_right,_rgba(251,191,36,0.18),_transparent_24%),linear-gradient(180deg,#f8fbff_0%,#ffffff_48%,#fffdf7_100%)]">
      <section id="restriction" className="relative px-4 pb-12 pt-28 md:px-6 md:pb-16 md:pt-32">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center"
          >
            <p className="text-sm font-semibold uppercase tracking-[0.38em] text-orange-500">Restricciones</p>
            <h1 className="mt-5 text-5xl font-black tracking-[-0.06em] text-slate-950 md:text-7xl">
              Cada zona, con su rango de edad
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-xl leading-9 text-slate-500">
              Consulta de forma visual qué espacio corresponde a cada edad para llegar al parque con la referencia clara desde el primer vistazo.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative px-4 pb-20 md:px-6">
        <div className="mx-auto grid max-w-6xl gap-10">
          {zones.map((zone) => (
            <motion.article
              key={zone.name}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className={`overflow-hidden rounded-[36px] border border-white/70 bg-gradient-to-br ${zone.accent} shadow-[0_28px_80px_rgba(15,23,42,0.08)]`}
            >
              <div className="grid items-stretch md:grid-cols-[1.15fr_0.85fr]">
                <div className="relative min-h-[320px] overflow-hidden">
                  <img
                    src={zone.image}
                    alt={zone.alt}
                    className="h-full w-full object-cover transition duration-500 hover:scale-[1.015]"
                  />
                </div>

                <div className="flex flex-col justify-between px-6 py-8 md:px-8 md:py-10">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-orange-500">{zone.name}</p>
                    <h2 className="mt-4 text-4xl font-black tracking-[-0.05em] text-slate-950 md:text-5xl">
                      {zone.age}
                    </h2>
                    <p className="mt-5 text-lg leading-8 text-slate-600">
                      {zone.description}
                    </p>
                  </div>

                  <div className="mt-8 space-y-3 border-t border-slate-200/70 pt-6">
                    {zone.notes.map((note) => (
                      <div key={note} className="flex items-start gap-3 text-slate-700">
                        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-orange-400" />
                        <p className="text-base leading-7">{note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
